import { z } from 'zod';

// Stores, suppliers and the stock ledger (docs/plans/phase1-stock.md §3).
// Quantities are whole numbers of the item's base unit; never fractions (ADR 0010).

/** The largest quantity one ledger line carries (Postgres `integer`). */
export const MAX_STOCK_LINE_QUANTITY = 2_147_483_647;

/** A whole, positive quantity of base units on one line. */
export const stockQuantitySchema = z.number().int().min(1).max(MAX_STOCK_LINE_QUANTITY);

export const STORE_KINDS = ['main', 'pharmacy', 'lab', 'other'] as const;
export type StoreKind = (typeof STORE_KINDS)[number];

export const storeSchema = z.object({
  id: z.uuid(),
  facilityId: z.uuid(),
  name: z.string(),
  kind: z.enum(STORE_KINDS),
  status: z.enum(['active', 'inactive']),
});

export type StoreView = z.infer<typeof storeSchema>;

const storeName = z.string().trim().min(1).max(80);

export const createStoreBodySchema = z.object({
  name: storeName,
  kind: z.enum(STORE_KINDS),
});

export const updateStoreBodySchema = z
  .object({
    name: storeName.optional(),
    status: z.enum(['active', 'inactive']).optional(),
  })
  .refine((body) => body.name !== undefined || body.status !== undefined, {
    message: 'Change the name or the status',
  });

export const supplierSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kraPin: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  status: z.enum(['active', 'inactive']),
});

export type SupplierView = z.infer<typeof supplierSchema>;

/** Empty text means "not given", so it is stored as null rather than as an empty string. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();

/**
 * KRA PINs are a letter, nine digits and a letter (e.g. P051234567X). Checked for shape
 * only: whether a PIN is registered is KRA's to say.
 */
const kraPin = z
  .string()
  .trim()
  .toUpperCase()
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .optional()
  .refine((value) => value === null || value === undefined || /^[A-Z]\d{9}[A-Z]$/.test(value), {
    message: 'A KRA PIN is a letter, nine digits and a letter, like P051234567X',
  });

export const createSupplierBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  kraPin,
  phone: optionalText(40),
  email: z
    .union([z.email().max(254), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional(),
});

export const updateSupplierBodySchema = createSupplierBodySchema.partial().extend({
  status: z.enum(['active', 'inactive']).optional(),
});

/** Supplier search is a POST body, like every other search (plan §3.3). */
export const supplierSearchBodySchema = z.object({
  query: z.string().trim().max(120).default(''),
  includeInactive: z.boolean().default(false),
  limit: z.number().int().min(1).max(50).default(20),
});
